import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, XCircle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";
import PillarCTAWithPhoto from "@/components/PillarCTAWithPhoto";

export const faqItems = [
  {
    question: "É normal ganhar peso quando se muda para a Austrália?",
    answer: "Sim, é muito comum. A mudança de rotina, o estresse da adaptação, os alimentos diferentes e a tendência a comer por conveniência contribuem para o ganho de peso. Isso não é culpa sua, é uma resposta natural do corpo a tantas mudanças."
  },
  {
    question: "Preciso fazer dieta restritiva para emagrecer na Austrália?",
    answer: "Não. Dietas restritivas costumam funcionar no curto prazo, mas não são sustentáveis. Meu acompanhamento foca em criar uma rotina alimentar que você consiga manter, sem cortar grupos alimentares e sem passar fome."
  },
  {
    question: "Em quanto tempo vou ver resultados?",
    answer: "Os acompanhamentos normalmente têm um período mínimo de 3 meses para ver resultados consistentes. Muitos pacientes relatam, já nas primeiras semanas, mais disposição e uma relação mais tranquila com a comida."
  },
  {
    question: "Posso emagrecer comendo arroz e feijão?",
    answer: "Sim. Arroz e feijão são alimentos nutritivos e podem fazer parte de um plano para emagrecimento. O segredo está nas quantidades, combinações e no equilíbrio geral das refeições."
  },
  {
    question: "Como lidar com a ansiedade por comida morando fora?",
    answer: "A ansiedade alimentar é muito comum entre imigrantes. No acompanhamento, trabalhamos estratégias práticas para lidar com ela, como planejar as refeições, não ficar longos períodos sem comer e entender os gatilhos emocionais."
  },
  {
    question: "O acompanhamento é só online ou tem presencial?",
    answer: "Atendo presencialmente em Brisbane e online para qualquer cidade da Austrália. A qualidade do acompanhamento é a mesma nos dois formatos."
  },
  {
    question: "Quanto custa o acompanhamento?",
    answer: "A consulta inicial dura cerca de 30 minutos e não tem custo. Nela conversamos sobre seus objetivos e eu apresento as opções de acompanhamento e os valores. Cada plano é personalizado."
  },
  {
    question: "Preciso ir à academia para emagrecer?",
    answer: "Não necessariamente. A atividade física ajuda, mas não precisa ser academia. Caminhar, nadar, pedalar ou qualquer movimento que você goste já faz diferença. O mais importante é a alimentação e a constância."
  },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button size="lg" variant="gradient" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto" style={{ background: 'var(--gradient-green)' }} onClick={openDialog}>
      <Calendar className="mr-2 h-5 w-5" />Agendar consulta
    </Button>
    <Button size="lg" variant="gradient-outline" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto" onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }} onClick={() => window.open('https://wa.me/61423038977', '_blank')}>
      <MessageCircle className="mr-2 h-5 w-5" />Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />
        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Clara Camille · Nutricionista</p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">Como emagrecer morando na Austrália: guia prático e realista</h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">Emagrecer morando fora do Brasil é possível, sem dietas extremas e sem sofrimento. Aqui você encontra um guia completo com estratégias práticas para a rotina australiana.</p>
              <CTAButtons openDialog={openDialog} />
            </div>
          </section>

          {/* CONTEÚDO PRINCIPAL */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="prose prose-lg max-w-none space-y-12">

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Por que imigrantes ganham peso na Austrália</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Se você ganhou peso depois de se mudar para a Austrália, saiba que isso é mais comum do que parece. Vários fatores contribuem para isso:</p>
                  <ul className="space-y-3">
                    {[
                      "Mudança de rotina: novos horários, menos tempo para cozinhar",
                      "Estresse da adaptação: saudade, pressão financeira, isolamento",
                      "Comida de conveniência: fast food barata e acessível",
                      "Produtos diferentes: dificuldade em encontrar o que comia no Brasil",
                      "Clima e estações invertidas: mudança nos padrões de fome e energia",
                      "Trabalhos físicos ou com horários irregulares que atrapalham a rotina alimentar",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Entender esses fatores é o primeiro passo. Não se trata de falta de disciplina, e sim de um contexto que dificulta as escolhas. E é exatamente isso que trabalhamos juntos no acompanhamento.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">A abordagem sem dieta restritiva</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Eu não trabalho com dietas restritivas porque elas não se sustentam a longo prazo. O que funciona é criar um plano alimentar que respeite a sua rotina, seus gostos e seu orçamento.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">No meu acompanhamento, o foco está em: escolher melhor os alimentos do dia a dia, organizar as refeições de forma prática, entender a fome e a saciedade e criar constância sem exigir perfeição.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">O objetivo não é seguir uma dieta por 30 dias. É construir uma forma de comer que você consiga manter por anos.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Exemplos de refeições para emagrecimento com produtos locais</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Aqui vão exemplos de refeições equilibradas que você monta com produtos dos mercados australianos. Lembre que são apenas exemplos gerais. O seu plano ideal é personalizado com base na sua rotina e nos seus objetivos.</p>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm sm:text-base">
                      <thead>
                        <tr className="bg-primary/10">
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Refeição</th>
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Opção 1</th>
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Opção 2</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Café da manhã</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Ovos mexidos com pão integral e abacate</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Iogurte grego com frutas e granola</td>
                        </tr>
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Almoço</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Arroz integral, feijão, frango grelhado e salada</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Bowl de quinoa com legumes e atum</td>
                        </tr>
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Lanche</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Banana com pasta de amendoim</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Mix de castanhas com frutas secas</td>
                        </tr>
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Jantar</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Sopa de legumes com frango desfiado</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Omelete com vegetais e pão integral</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <PillarCTAWithPhoto
                  title="Essas opções são só exemplos"
                  message="No acompanhamento, eu monto um plano personalizado com refeições que funcionam para a sua rotina, o seu orçamento e os seus gostos."
                  openDialog={openDialog}
                />

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Erros comuns que dificultam o emagrecimento</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Na minha prática com pacientes brasileiros na Austrália, vejo alguns padrões que se repetem:</p>
                  <ul className="space-y-3">
                    {[
                      "Pular refeições pensando que isso ajuda a emagrecer",
                      "Trocar refeições por snacks ultraprocessados por falta de tempo",
                      "Começar dietas extremas que duram poucos dias",
                      "Não beber água suficiente, especialmente no verão",
                      "Comer por ansiedade ou tédio, sem perceber",
                      "Comparar o próprio corpo com o de antes sem considerar o contexto",
                      "Achar que precisa comer perfeitamente 100% do tempo",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">O que funciona na prática</h2>
                  <ul className="space-y-3">
                    {[
                      "Planejar as refeições da semana com antecedência",
                      "Fazer meal prep no domingo para ter comida pronta",
                      "Comer de 3 a 4 vezes ao dia sem longos intervalos",
                      "Priorizar proteínas e vegetais em todas as refeições",
                      "Beber pelo menos 2 litros de água por dia",
                      "Ter um acompanhamento profissional que entenda sua realidade",
                      "Focar em constância, não em perfeição",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Compras inteligentes para emagrecer gastando pouco</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Emagrecer na Austrália não precisa ser caro. Com compras inteligentes, você consegue montar refeições equilibradas sem estourar o orçamento. Priorize frutas e vegetais da estação, proteínas em promoção e marcas próprias dos mercados.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Acompanhar as promoções semanais do Coles e Woolworths (pelo app ou pelo site) e comprar no Aldi para itens básicos são duas estratégias simples que fazem muita diferença no final do mês.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">A importância do acompanhamento profissional</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Emagrecer sozinha é possível, mas é bem mais difícil. Ter uma nutricionista que entende a sua realidade de imigrante, que fala a sua língua e que monta um plano com os produtos que você encontra aqui faz toda a diferença.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">No meu acompanhamento, você tem plano personalizado, suporte contínuo por WhatsApp, ajustes conforme sua rotina muda e uma escuta atenta às suas dificuldades. Os acompanhamentos normalmente têm um período mínimo de 3 meses para ver resultados consistentes.</p>
                </div>

                <PillarCTAWithPhoto
                  title="Não precisa fazer tudo sozinha"
                  message="Emagrecer morando fora é difícil, mas com o acompanhamento certo fica mais simples. Vamos conversar sobre o que faz sentido para você."
                  openDialog={openDialog}
                />

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Exercício físico: precisa ou não precisa?</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Exercício ajuda, mas não é obrigatório para emagrecer. O mais importante é a alimentação. Dito isso, a Austrália oferece muitas opções gratuitas e acessíveis de atividade física: caminhar na praia, trilhas, parques com equipamentos públicos, piscinas comunitárias.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">O segredo é encontrar algo que você goste e consiga manter na rotina. Não precisa ser academia se você não gosta de academia.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Leia também no blog</h2>
                  <ul className="space-y-3">
                    <li><Link to="/blog/$slug" params={{ slug: "emagrecimento-sem-dieta-maluca-como-ter-constancia" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Emagrecimento sem dieta maluca: como ter constância</Link></li>
                    <li><Link to="/blog/$slug" params={{ slug: "por-que-morar-fora-bagunda-alimentacao-e-como-voltar-ao-eixo" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Por que morar fora bagunça sua alimentação e como voltar ao eixo</Link></li>
                    <li><Link to="/blog/$slug" params={{ slug: "ansiedade-e-comida-na-adaptacao-como-sair-do-8-ou-80" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Ansiedade e comida na adaptação: como sair do 8 ou 80</Link></li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Tire suas dúvidas</p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">Perguntas frequentes</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} className="rounded-xl border border-border/50 bg-card px-5 sm:px-6">
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">{item.question}</AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div className="absolute inset-0 blur-sm scale-105" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16" style={{ background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)', border: '1px solid rgba(255, 255, 255, 0.25)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                <img src={claraPortrait} alt="Clara Camille" className="w-20 h-20 rounded-full object-cover object-top border-2 border-white/50 shadow-lg mx-auto" loading="lazy" />
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">Quer começar a cuidar da sua alimentação?</h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">Na consulta inicial, entendo sua rotina e seus objetivos e conversamos sobre como emagrecer de forma saudável e realista aqui na Austrália.</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button size="lg" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto" onClick={openDialog}><Calendar className="mr-2 h-5 w-5" />Agendar consulta</Button>
                  <Button size="lg" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto" style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }} onClick={() => window.open('https://wa.me/61423038977', '_blank')}><MessageCircle className="mr-2 h-5 w-5" />Falar no WhatsApp</Button>
                </div>
              </div>
            </div>
          </section>

          {/* LINKS INTERNOS */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">Navegue pelo site:</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Página inicial</Link>
                <span className="text-border">·</span>
                <Link to="/guia-alimentacao-brasileiros-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Guia de alimentação</Link>
                <span className="text-border">·</span>
                <Link to="/acompanhamento-nutricional-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Acompanhamento</Link>
                <span className="text-border">·</span>
                <Link to="/alimentacao-saudavel-hospitality-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Hospitality</Link>
                <span className="text-border">·</span>
                <Link to="/dieta-estudantes-brasileiros-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Estudantes</Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const ComoEmagrecerMorandoAustralia = () => (
  <ScheduleProvider><PageContent /></ScheduleProvider>
);

export default ComoEmagrecerMorandoAustralia;
